/**
 * TermsOfServicePage — bilingual terms of service for NoJo Enterprise.
 *
 * Style notes (Feb 2026):
 *   - Condensed inline header — matches the Privacy Policy page layout
 *     (single line with title + dynamic JST date), no oversized hero.
 *   - Structure consolidated from JapanETCcard.com/terms and
 *     JapanTollReceipts.jp/en/terms, normalized to the NoJo Enterprise
 *     corporate parent level. Each operated service may carry its own
 *     additional service-specific terms on its own site; these terms
 *     cover use of www.nojoenterprise.com and the general framework that
 *     applies across operated services.
 *   - Avoids naming specific vendors (no Mandrill, MongoDB, Stripe, etc.).
 *   - Banned wording confirmed absent: "Financial Services", "FSA",
 *     "bank-grade", "remittance".
 */
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Mail } from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import PageSideAura from "@/components/motion/PageSideAura";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

function formatDate(lang) {
  // Always reflect today (JST) — the document is treated as live-managed.
  const d = new Date();
  return new Intl.DateTimeFormat(lang === "ja" ? "ja-JP" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "Asia/Tokyo",
  }).format(d);
}

// ─────────────────────────────────────────────────────────────────────────────
// Section copy
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS_EN = [
  {
    heading: "1. Acceptance of Terms",
    paragraph:
      "By accessing the NoJo Enterprise corporate website (www.nojoenterprise.com) or by using any service operated by NoJo Enterprise Co., Ltd. (collectively, the \"Services\"), you agree to be bound by these Terms of Service together with our Privacy Policy. If you do not agree, please discontinue use of the Services.",
  },
  {
    heading: "2. About NoJo Enterprise & Operated Services",
    paragraph:
      "NoJo Enterprise Co., Ltd. (株式会社NoJo Enterprise) is an independent company registered in Yokosuka, Japan. We operate JapanETCcard.com (ETC card support service), JapanTollReceipts.jp (automated toll-record reporting service) and the planned JapanChangeMoney.com (currency exchange service). Each operated service runs on its own dedicated domain and may publish its own service-specific terms; those service-specific terms govern that particular service in addition to the corporate-level terms set out here.",
  },
  {
    heading: "3. Independence from Public Authorities",
    paragraph:
      "Neither NoJo Enterprise nor any of its operated services is affiliated with NEXCO, the ETC Usage Inquiry Service (MEISAI), any toll-road operator's official issuance or settlement program, or any government agency. All references to such organizations on our sites are for clarity only and do not imply endorsement or partnership.",
  },
  {
    heading: "4. Eligibility",
    paragraph:
      "Use of our corporate website is open to all visitors. Use of an operated service may require that you (i) are at least 18 years of age, (ii) provide accurate registration information, and (iii) have the legal authority to enter into a binding agreement in your jurisdiction. Each operated service may set additional eligibility criteria; please review the service-specific terms on its own site.",
  },
  {
    heading: "5. Account & Security",
    bullets: [
      "You are responsible for safeguarding any account credentials issued to you by an operated service and for all activity that occurs under those credentials.",
      "Notify us immediately of any suspected unauthorized access. We are not liable for losses arising from your failure to maintain the confidentiality of your credentials.",
      "We may suspend or terminate access where credentials have been compromised, where required by law, or to protect the integrity of the Services.",
    ],
  },
  {
    heading: "6. Acceptable Use",
    bullets: [
      "Do not use the Services for any unlawful purpose or in any way that infringes the rights of others.",
      "Do not attempt to access non-public areas of the Services, probe or test the vulnerability of our systems, or interfere with normal operation.",
      "Do not impersonate any person or entity, misrepresent your affiliation with us, or use the Services to send spam, malware, or fraudulent communications.",
      "Do not reverse-engineer, scrape automatically, or attempt to derive the source code of any part of the Services beyond what is expressly permitted.",
    ],
  },
  {
    heading: "7. Fees & Service-Specific Terms",
    paragraph:
      "Access to this corporate website is free. Fees, billing cycles, free-trial terms and refund policies for each operated service are set out on that service's own website and in its service-specific terms. Where a discrepancy exists between these corporate-level terms and a service-specific term, the service-specific term controls for that service.",
  },
  {
    heading: "8. Intellectual Property",
    paragraph:
      "All content on the Services — including text, graphics, logos, wordmarks, software, design, and the structure of the site — is owned by NoJo Enterprise Co., Ltd. or its licensors, and is protected by copyright, trademark, and other applicable laws. You may not copy, modify, distribute, or create derivative works without our prior written permission, except for personal, non-commercial use of the content as displayed.",
  },
  {
    heading: "9. Service Availability",
    paragraph:
      "We aim to keep the Services available at all times but do not guarantee uninterrupted access. Scheduled maintenance, urgent security work, third-party outages, and force-majeure events may cause temporary unavailability. We will provide advance notice of planned maintenance where reasonably practicable.",
  },
  {
    heading: "10. Disclaimers",
    paragraph:
      "The Services are provided on an \"as is\" and \"as available\" basis, without warranties of any kind, express or implied, to the maximum extent permitted by applicable law. We do not warrant that the Services will be error-free, secure, or always available, or that information provided will be accurate or current at every moment.",
  },
  {
    heading: "11. Limitation of Liability",
    paragraph:
      "To the maximum extent permitted by law, NoJo Enterprise Co., Ltd., its directors, officers, employees, and contractors shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or business opportunity, arising out of or in connection with your use of the Services. Our aggregate liability arising under or in connection with these Terms shall not exceed the amount paid by you to us for the Services during the twelve (12) months immediately preceding the event giving rise to the claim, or, if no amount was paid, JPY 5,000.",
  },
  {
    heading: "12. Indemnification",
    paragraph:
      "You agree to indemnify and hold NoJo Enterprise Co., Ltd. and its representatives harmless from any claim, demand, loss, liability, or expense (including reasonable legal fees) arising out of your use of the Services, your violation of these Terms, or your violation of any applicable law or the rights of any third party.",
  },
  {
    heading: "13. Suspension & Termination",
    paragraph:
      "We may suspend or terminate your access to all or part of the Services at any time, with or without notice, where required by law, where you have breached these Terms, or to protect other users or the integrity of the Services. Upon termination, sections that by their nature should survive (Intellectual Property, Disclaimers, Limitation of Liability, Indemnification, Governing Law) will continue to apply.",
  },
  {
    heading: "14. Changes to These Terms",
    paragraph:
      "We may update these Terms from time to time. Material changes will be reflected on this page with a revised \"Last updated\" date and, where appropriate, communicated to active customers through the operated service. Continued use of the Services after a change becomes effective constitutes acceptance of the updated Terms.",
  },
  {
    heading: "15. Governing Law & Jurisdiction",
    paragraph:
      "These Terms are governed by the laws of Japan. The Yokohama District Court (横浜地方裁判所) shall have exclusive jurisdiction of the first instance over any dispute arising out of or in connection with these Terms or the Services.",
  },
];

const SECTIONS_JA = [
  {
    heading: "1. 本規約への同意",
    paragraph:
      "NoJo Enterpriseコーポレートサイト（www.nojoenterprise.com）へのアクセス、または株式会社NoJo Enterpriseが運営する各種サービス（以下、総称して「本サービス」）のご利用にあたり、お客様は本利用規約および当社プライバシーポリシーに同意したものとみなします。ご同意いただけない場合は、本サービスのご利用をお控えください。",
  },
  {
    heading: "2. NoJo Enterpriseおよび運営サービスについて",
    paragraph:
      "株式会社NoJo Enterpriseは、神奈川県横須賀市に登記された独立した法人です。当社は、JapanETCcard.com（ETCカードサポートサービス）、JapanTollReceipts.jp（ETC利用明細レポート自動配信サービス）、および準備中のJapanChangeMoney.com（外貨両替関連サービス）を運営しています。各運営サービスは固有のドメイン上で運用されており、サービス固有の利用規約を別途定める場合があります。当該サービス固有の規約は、本コーポレートレベルの規約に加え、当該サービスについて適用されます。",
  },
  {
    heading: "3. 公的機関との独立性",
    paragraph:
      "NoJo Enterprise本体および各運営サービスはいずれも、NEXCO、ETC利用照会サービス（MEISAI）、各高速道路事業者の公式発行・精算プログラム、その他いかなる行政機関とも提携していません。当社サイト上におけるこれら組織への言及は、説明上の参照に過ぎず、提携・推奨関係を示すものではありません。",
  },
  {
    heading: "4. 利用資格",
    paragraph:
      "コーポレートサイトのご利用は、どなたにも開かれています。運営サービスのご利用には、（i）満18歳以上であること、（ii）正確な登録情報を提供いただくこと、（iii）お客様の管轄区域において契約締結能力を有すること、が必要となる場合があります。各運営サービスは追加の利用資格要件を設けることがあります。詳細は各サービス固有の規約をご確認ください。",
  },
  {
    heading: "5. アカウントとセキュリティ",
    bullets: [
      "運営サービスにおいて発行された認証情報の管理、および当該認証情報を用いて行われたすべての行為について、お客様自身がご責任を負うものとします。",
      "認証情報が第三者に漏洩・不正使用された疑いがある場合は、直ちに当社までご連絡ください。お客様が認証情報の秘密を保持しなかったことに起因する損害について、当社は責任を負いません。",
      "認証情報の漏洩、法令上の要請、または本サービスの安全性と健全な運用を確保するため、当社はアクセスの停止または終了を行うことがあります。",
    ],
  },
  {
    heading: "6. 禁止事項",
    bullets: [
      "違法な目的、または他者の権利を侵害する形での本サービスの利用。",
      "本サービスの非公開領域への不正アクセス、システム脆弱性の探査・試行、または通常運用の妨害行為。",
      "他人または法人へのなりすまし、当社との関係性の虚偽表示、本サービスを介したスパム・マルウェア・詐欺的通信の送信。",
      "本サービスの一部に対するリバースエンジニアリング、自動的・組織的な不正取得、ソースコードの不正取得など、明示的に許可された範囲を超える行為。",
    ],
  },
  {
    heading: "7. 利用料金とサービス固有規約",
    paragraph:
      "本コーポレートサイトへのアクセスは無料です。各運営サービスの利用料、課金サイクル、無料トライアル条件、返金ポリシーなどは、当該サービスのウェブサイトおよびサービス固有の利用規約に定められています。本コーポレートレベルの規約と各サービス固有規約に齟齬がある場合、当該サービスについては、サービス固有規約が優先します。",
  },
  {
    heading: "8. 知的財産権",
    paragraph:
      "本サービス上のすべてのコンテンツ（テキスト、グラフィック、ロゴ、ワードマーク、ソフトウェア、デザイン、サイト構造を含みます）は、株式会社NoJo Enterpriseまたはそのライセンサーに帰属し、著作権法、商標法その他の法令により保護されています。表示された範囲での個人的・非商業的利用を除き、当社の事前の書面による許諾なく、複製、改変、配布、二次的著作物の作成を行うことはできません。",
  },
  {
    heading: "9. サービスの提供可能性",
    paragraph:
      "当社は本サービスを安定して提供できるよう努めますが、無中断のアクセスを保証するものではありません。計画的メンテナンス、緊急のセキュリティ対応、第三者起因の障害、不可抗力事象などにより、一時的に利用できなくなる場合があります。計画的メンテナンスについては、合理的に可能な範囲で事前に通知いたします。",
  },
  {
    heading: "10. 免責事項",
    paragraph:
      "本サービスは、適用法令の許容する最大限の範囲において、明示・黙示を問わずいかなる種類の保証もなく「現状有姿」「提供可能な範囲」で提供されます。当社は、本サービスがエラーのないものであること、安全であること、常時提供されること、提供される情報が常に正確かつ最新であることを保証するものではありません。",
  },
  {
    heading: "11. 責任の制限",
    paragraph:
      "法令で許容される最大限の範囲において、株式会社NoJo Enterpriseおよびその役員、従業員、業務委託先は、本サービスのご利用に起因または関連して生じる間接的、付随的、特別、結果的または懲罰的損害、ならびに逸失利益、収益、データ、事業機会の損失について、責任を負いません。本規約に基づく当社の累計責任額は、当該請求の原因事象発生直前12か月間にお客様が当社へ支払った金額（支払いがない場合は5,000円）を上限とします。",
  },
  {
    heading: "12. 補償",
    paragraph:
      "お客様による本サービスの利用、本規約の違反、適用法令または第三者の権利の侵害に起因または関連して生じる請求、要求、損失、責任、費用（合理的な弁護士費用を含みます）について、株式会社NoJo Enterpriseおよびその関係者を補償し、損害を被らせないものとします。",
  },
  {
    heading: "13. 利用停止および終了",
    paragraph:
      "法令上要請される場合、お客様が本規約に違反した場合、または他のユーザー・本サービスの完全性を保護する必要がある場合、当社は事前の通知の有無に関わらず、本サービスの全部または一部のご利用を停止または終了することができます。終了後も、性質上存続すべき条項（知的財産権、免責事項、責任の制限、補償、準拠法）は引き続き効力を有します。",
  },
  {
    heading: "14. 本規約の変更",
    paragraph:
      "当社は本規約を随時改定する場合があります。重要な変更は、本ページ上に最終更新日を更新して掲示するとともに、必要に応じて運営サービスをご利用中のお客様にもご案内します。変更の効力発生後も本サービスをご利用いただいた場合、変更後の規約に同意いただいたものとみなします。",
  },
  {
    heading: "15. 準拠法および裁判管轄",
    paragraph:
      "本規約は、日本法に準拠し、解釈されます。本規約または本サービスに起因または関連して生じる一切の紛争については、横浜地方裁判所を第一審の専属的合意管轄裁判所とします。",
  },
];

// ─────────────────────────────────────────────────────────────────────────────

export default function TermsOfServicePage() {
  const { lang = "en" } = useParams();
  const isJa = lang === "ja";
  const sections = isJa ? SECTIONS_JA : SECTIONS_EN;

  return (
    <article data-testid="terms-page" className="bg-white">
      <SEO {...PAGE_SEO.terms[lang]} path={`/${lang}/legal/terms`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "利用規約" : "Terms of Service", url: `/${lang}/legal/terms` },
        ])}
      />

      {/* Condensed inline header — matches Privacy page. */}
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-4 md:py-5">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1
              data-testid="terms-page-h1"
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-[30px] font-bold tracking-tight leading-[1.1]",
              ].join(" ")}
            >
              {isJa ? "利用規約" : "Terms of Service"}
            </h1>
            <span className="text-[#CBD5E1]">·</span>
            <span className="text-[12.5px] tracking-[0.04em] text-[#94A3B8] font-mono-display">
              {isJa
                ? `最終更新日: ${formatDate("ja")} (JST)`
                : `Last updated: ${formatDate("en")} (JST)`}
            </span>
          </div>
        </div>
      </section>

      <section className="relative isolate">
        <PageSideAura testId="terms-body-aura" />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-10 space-y-7 md:space-y-9">
          {sections.map((s, i) => (
            <div key={i} data-testid={`terms-section-${i}`}>
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-[18px] md:text-[20px] font-bold tracking-tight leading-snug mb-3",
                ].join(" ")}
              >
                {s.heading}
              </h2>
              {s.paragraph && (
                <p className="text-[14.5px] leading-[1.85] text-[#334155]">{s.paragraph}</p>
              )}
              {s.bullets && (
                <ul className="space-y-2.5 text-[14.5px] leading-[1.85] text-[#334155] mt-2">
                  {s.bullets.map((line, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <span
                        aria-hidden
                        className="mt-2 h-1.5 w-1.5 rounded-full bg-[#0E5FB0] shrink-0"
                      />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Contact strip — concise. */}
      <section className="border-t border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-10">
          <h2
            className={[
              isJa ? "font-ja" : "font-display",
              "text-[#0F1D2D] text-[18px] md:text-[20px] font-bold tracking-tight leading-snug mb-3",
            ].join(" ")}
          >
            {isJa ? "16. お問い合わせ" : "16. Contact"}
          </h2>
          <p className="text-[14.5px] leading-[1.85] text-[#334155] mb-4">
            {isJa
              ? "本利用規約に関するご質問、ご要望、ご意見は下記までお寄せください。"
              : "Questions or concerns about these Terms? Please reach out using the details below."}
          </p>
          <ul className="space-y-1.5 text-[14px] leading-[1.7] text-[#334155]">
            <li>
              <span className="font-mono-display text-[12px] tracking-[0.18em] uppercase text-[#94A3B8] mr-2">
                {isJa ? "メール" : "Email"}
              </span>
              <a
                href="mailto:support@nojoenterprise.com?subject=Terms%20of%20Service%20inquiry"
                className="text-[#0E5FB0] hover:underline"
                data-testid="terms-email-link"
              >
                support@nojoenterprise.com
              </a>
            </li>
            <li>
              <span className="font-mono-display text-[12px] tracking-[0.18em] uppercase text-[#94A3B8] mr-2">
                {isJa ? "電話" : "Phone"}
              </span>
              <a
                href="tel:+81468747156"
                className="text-[#0E5FB0] hover:underline"
                data-testid="terms-phone-link"
              >
                046-874-7156
              </a>
            </li>
            <li>
              <span className="font-mono-display text-[12px] tracking-[0.18em] uppercase text-[#94A3B8] mr-2">
                {isJa ? "所在地" : "Office"}
              </span>
              {isJa
                ? "〒238-0041 神奈川県横須賀市本町 2-2-1 髙村ビル #202"
                : "#202 Takamura Building, 2-2-1 Honcho, Yokosuka 238-0041, Kanagawa, Japan"}
            </li>
          </ul>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/${lang}/contact`}
              data-testid="terms-contact-cta"
              className="inline-flex items-center gap-2 bg-[#0E5FB0] text-white px-5 py-3 rounded-full text-[13px] font-semibold tracking-tight hover:bg-[#0B4D8F] transition-colors"
            >
              {isJa ? "お問い合わせフォーム" : "Use the contact form"}
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Link>
            <Link
              to={`/${lang}/legal/privacy`}
              data-testid="terms-privacy-cta"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full ring-1 ring-[#E2E8F0] bg-white text-[#0F1D2D] text-[13px] font-semibold tracking-tight hover:bg-[#F8F9FA] transition-colors"
            >
              <Mail className="h-3.5 w-3.5" strokeWidth={2.25} />
              {isJa ? "プライバシーポリシー" : "Privacy Policy"}
              <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.25} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}
